import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  TablePagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import {
  useGetDamageReportsQuery,
  useReportDamageMutation,
  useGetLocationsQuery,
} from '../../../store/api/inventoryApi';
import ProductAutocomplete from '../../../shared/components/ProductAutocomplete';
import { format } from 'date-fns';

function DamageReporting() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: reportsData, isLoading, refetch } = useGetDamageReportsQuery({
    page,
    limit,
  });

  const { data: locations } = useGetLocationsQuery();
  const [reportDamage, { isLoading: isSubmitting }] = useReportDamageMutation();


  const handleReportDamage = async () => {
    if (!selectedProduct || !selectedLocation || !quantity) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await reportDamage({
        productId: selectedProduct.id,
        locationId: selectedLocation,
        quantity: parseFloat(quantity),
        reason,
        date: new Date(date).toISOString(),
      }).unwrap();
      
      setReportDialogOpen(false);
      resetForm();
      refetch();
    } catch (error) {
      alert(error?.data?.message || 'Error reporting damage');
    }
  };

  const resetForm = () => {
    setSelectedProduct(null);
    setSelectedLocation('');
    setQuantity('');
    setReason('');
    setDate(new Date().toISOString().split('T')[0]);
  };

  const reports = reportsData?.reports || [];
  const pagination = reportsData?.pagination;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Product Damage Reports
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setReportDialogOpen(true)}
        >
          Report Damage
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reported By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.length > 0 ? (
                  reports.map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>{format(new Date(report.date), 'dd/MM/yyyy')}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{report.product?.name}</TableCell>
                      <TableCell>{report.location?.name}</TableCell>
                      <TableCell>{report.quantity}</TableCell>
                      <TableCell>{report.reason || '-'}</TableCell>
                      <TableCell>
                        {report.user ? `${report.user.firstName || ''} ${report.user.lastName || ''}`.trim() || report.user.username : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {isLoading ? 'Loading reports...' : 'No damage reports found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {pagination && pagination.pages > 1 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={(pagination.page || 1) - 1}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              rowsPerPage={pagination.limit || limit}
              onRowsPerPageChange={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setPage(1);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 2 }}
            />
          )}
        </CardContent>
      </Card>

      {/* Report Damage Dialog */}
      <Dialog 
        open={reportDialogOpen} 
        onClose={() => !isSubmitting && setReportDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Report Product Damage</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ProductAutocomplete
              value={selectedProduct}
              onChange={(e, newValue) => setSelectedProduct(newValue)}
              label="Search Product (Name, SKU, Barcode, Brand)"
              required
            />

            <TextField
              fullWidth
              select
              label="Location"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              required
            >
              {locations?.map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              required
              inputProps={{ min: 0.01, step: 0.01 }}
            />

            <TextField
              fullWidth
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Reason / Notes"
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Expired, Broken, Water damage..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleReportDamage} 
            variant="contained" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Reporting...' : 'Submit Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DamageReporting;
